import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { personenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { Button } from './ui/primitives'

/** Dieselbe Schranke wie die NetCon-Anmeldung (`NETCON_ANMELDE_DOMAIN` im API-Paket, E-224). */
const DOMAIN = '@epcan.de'

/**
 * **Beteiligte eines Vorhabens – und der Weg, jemanden dazuzuholen**
 * ([E-440](../../../docs/ENTSCHEIDUNGEN.md#e-440), 23.10.2026).
 *
 * Nils: *„Ich möchte bei Vorhaben jemanden einladen können (per Mail, aber NetCon-User muss das
 * sein), dass er das Vorhaben mit Beschreibung und so füllt."*
 *
 * Angeboten wird, wer eine Adresse auf `@epcan.de` hat – der Intranet-Abgleich hat fast alle
 * schon angelegt (E-355). Wer fehlt, wird per Adresse eingeladen; der Server legt ihn als Gast
 * an, und die erste NetCon-Anmeldung findet genau diese Person.
 *
 * Inline statt als zweites Fenster über dem Vorhaben-Fenster: Einladen ist ein Handgriff, und
 * die Liste der Beteiligten soll dabei sichtbar bleiben.
 */
export interface Beteiligter {
  personId: string
  name: string
  mailAusgang: string | null
  mailAm: Date | string | null
}

/**
 * **Was aus der Einladungsmail wurde – in Worten** (23.10.2026). Nils: *„wie sehe ich, dass er
 * eine kriegt?"* Die Werte kommen aus dem Versand (`VersandAusgang` im API-Paket).
 */
export function mailStand(b: Pick<Beteiligter, 'mailAusgang' | 'mailAm'>): {
  text: string
  gut: boolean | null
} {
  const wann = b.mailAm
    ? new Date(b.mailAm).toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''
  switch (b.mailAusgang) {
    case null:
      return { text: 'keine Mail', gut: null }
    case 'wartet':
      return { text: 'Mail wird verschickt …', gut: null }
    case 'gesendet':
      return { text: `Mail gesendet ${wann}`, gut: true }
    case 'probe_umgeleitet':
      return { text: `Probebetrieb: an die Probeadresse ${wann}`, gut: false }
    case 'probe_unterdrueckt':
    case 'kein_zugang':
      return { text: 'nicht gesendet – Mailversand ist nicht eingerichtet', gut: false }
    case 'keine_adresse':
      return { text: 'nicht gesendet – keine Adresse', gut: false }
    case 'person_inaktiv':
      return { text: 'nicht gesendet – Person deaktiviert', gut: false }
    case 'fehler':
      return { text: `Versand gescheitert ${wann} – wird erneut versucht`, gut: false }
    default:
      return { text: `nicht gesendet (${b.mailAusgang})`, gut: false }
  }
}

/**
 * **Beteiligte eines Vorhabens – und der Weg, jemanden dazuzuholen**
 * ([E-440](../../../docs/ENTSCHEIDUNGEN.md#e-440), 23.10.2026).
 *
 * Nils: *„Ich möchte bei Vorhaben jemanden einladen können (per Mail, aber NetCon-User muss das
 * sein), dass er das Vorhaben mit Beschreibung und so füllt."*
 *
 * Angeboten wird, wer eine Adresse auf `@epcan.de` hat – der Intranet-Abgleich hat fast alle
 * schon angelegt (E-355). Wer fehlt, wird per Adresse eingeladen; der Server legt ihn als Gast
 * an, und die erste NetCon-Anmeldung findet genau diese Person.
 *
 * Jede Einladung trägt ihren **Mailstand**, und solange eine Mail unterwegs ist, fragt die Ansicht
 * nach ein paar Sekunden erneut – die Antwort auf „kriegt er eine?" soll dastehen, ohne Neuladen.
 *
 * Steht im Vorhaben-Detail und im Einstellungs-Panel (dort neben dem Mit-Owner, wo man es sucht).
 */
export function VorhabenBeteiligte({
  vorhabenId,
  beteiligte,
  mitOwnerPersonId,
  mitOwnerName,
  darfEinladen,
}: {
  vorhabenId: string
  beteiligte: Beteiligter[]
  mitOwnerPersonId: string | null
  /** Für den Hinweis „Mit-Owner, aber nicht eingeladen". */
  mitOwnerName?: string | null
  darfEinladen: boolean
}) {
  const queryClient = useQueryClient()
  const [offen, setOffen] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  const neuLaden = () => queryClient.invalidateQueries({ queryKey: ['vorhaben'] })

  const ausladen = useMutation({
    mutationFn: (personId: string) => trpc.vorhaben.ausladen.mutate({ vorhabenId, personId }),
    onSuccess: neuLaden,
  })
  const einladen = useMutation({
    mutationFn: (eingabe: { personId: string; alsMitOwner: boolean }) =>
      trpc.vorhaben.einladen.mutate({ vorhabenId, ...eingabe }),
    onSuccess: async () => {
      setFehler(null)
      await neuLaden()
    },
    onError: (e: Error) => setFehler(e.message),
  })

  const unterwegs = beteiligte.some((b) => b.mailAusgang === 'wartet')
  useEffect(() => {
    if (!unterwegs) return
    const t = setTimeout(() => void neuLaden(), 4000)
    return () => clearTimeout(t)
  }, [unterwegs, beteiligte])

  const mitOwnerFehlt =
    darfEinladen && mitOwnerPersonId && !beteiligte.some((b) => b.personId === mitOwnerPersonId)

  if (!darfEinladen && beteiligte.length === 0) return null

  return (
    <div className="mt-1.5 text-[11px] text-muted">
      {mitOwnerFehlt && (
        <div className="mb-2 rounded-lg bg-[#fdf3dc] px-3 py-2 text-[11.5px] leading-relaxed text-[#8a6100]">
          <strong>{mitOwnerName ?? 'Der Mit-Owner'}</strong> ist nur eingetragen, nicht eingeladen:
          bekommt keine Mail und kann das Vorhaben nicht öffnen.{' '}
          <button
            type="button"
            disabled={einladen.isPending}
            onClick={() => einladen.mutate({ personId: mitOwnerPersonId, alsMitOwner: true })}
            className="cursor-pointer font-semibold text-accent-deep hover:underline"
          >
            {einladen.isPending ? 'Lädt ein …' : 'Einladen & Mail schicken'}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>Eingeladen:</span>
        {beteiligte.length === 0 && <span>niemand</span>}
        {beteiligte.map((b) => {
          const stand = mailStand(b)
          return (
            <span
              key={b.personId}
              className="inline-flex flex-wrap items-center gap-1 rounded-md bg-[#f3f2ef] px-1.5 py-px text-ink2"
            >
              {b.name}
              {b.personId === mitOwnerPersonId && <span className="text-muted">· Mit-Owner</span>}
              <span
                className={
                  stand.gut === true
                    ? 'text-[#2f7a3b]'
                    : stand.gut === false
                      ? 'text-pool-sofort'
                      : 'text-muted'
                }
              >
                · ✉ {stand.text}
              </span>
              {darfEinladen && b.mailAusgang !== 'wartet' && (
                <button
                  type="button"
                  disabled={einladen.isPending}
                  onClick={() =>
                    einladen.mutate({
                      personId: b.personId,
                      alsMitOwner: b.personId === mitOwnerPersonId,
                    })
                  }
                  className="cursor-pointer text-accent-deep hover:underline"
                  title="Die Einladungsmail noch einmal schicken"
                >
                  erneut senden
                </button>
              )}
              {darfEinladen && (
                <button
                  type="button"
                  onClick={() => ausladen.mutate(b.personId)}
                  className="cursor-pointer text-muted hover:text-pool-sofort"
                  aria-label={`${b.name} ausladen`}
                  title="Einladung zurücknehmen"
                >
                  ✕
                </button>
              )}
            </span>
          )
        })}
        {darfEinladen && !offen && (
          <button
            type="button"
            onClick={() => {
              setMeldung(null)
              setOffen(true)
            }}
            className="cursor-pointer text-accent-deep hover:underline"
          >
            + einladen
          </button>
        )}
        {meldung && <span className="text-ink2">{meldung}</span>}
        {fehler && <span className="text-pool-sofort">{fehler}</span>}
      </div>

      {offen && (
        <EinladenFormular
          vorhabenId={vorhabenId}
          schonDabei={new Set(beteiligte.map((b) => b.personId))}
          fertig={(text) => {
            setOffen(false)
            setMeldung(text)
          }}
          abbrechen={() => setOffen(false)}
        />
      )}
    </div>
  )
}

function EinladenFormular({
  vorhabenId,
  schonDabei,
  fertig,
  abbrechen,
}: {
  vorhabenId: string
  schonDabei: Set<string>
  fertig: (meldung: string) => void
  abbrechen: () => void
}) {
  const queryClient = useQueryClient()
  const { data: personen = [] } = useQuery(personenQuery)
  const [suche, setSuche] = useState('')
  const [gewaehlt, setGewaehlt] = useState<{
    personId?: string
    email?: string
    name: string
  } | null>(null)
  const [alsMitOwner, setAlsMitOwner] = useState(false)
  const [nachricht, setNachricht] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const treffer = useMemo(() => {
    const s = suche.trim().toLowerCase()
    if (s.length < 2) return []
    return personen
      .filter((p) => p.email?.toLowerCase().endsWith(DOMAIN) && !schonDabei.has(p.id))
      .filter((p) => p.displayName.toLowerCase().includes(s) || p.email?.toLowerCase().includes(s))
      .slice(0, 6)
  }, [personen, suche, schonDabei])

  const adresse = suche.trim().toLowerCase()
  const adresseGueltig = /^[^\s@]+@[^\s@]+$/.test(adresse) && adresse.endsWith(DOMAIN)
  const adresseBekannt = personen.some((p) => p.email?.toLowerCase() === adresse)

  const einladen = useMutation({
    mutationFn: () => {
      if (!gewaehlt) throw new Error('Erst eine Person wählen.')
      return trpc.vorhaben.einladen.mutate({
        vorhabenId,
        ...(gewaehlt.personId ? { personId: gewaehlt.personId } : {}),
        ...(gewaehlt.email ? { email: gewaehlt.email } : {}),
        alsMitOwner,
        ...(nachricht.trim() ? { nachricht: nachricht.trim() } : {}),
      })
    },
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
      await queryClient.invalidateQueries({ queryKey: ['people'] })
      fertig(`✓ ${r.name} eingeladen.`)
    },
    onError: (e: Error) => setFehler(e.message),
  })

  return (
    <div className="mt-2 rounded-lg border border-line bg-surface p-2.5 text-[12px] text-ink">
      {gewaehlt ? (
        <div className="flex flex-wrap items-center gap-2">
          <span>
            <strong>{gewaehlt.name}</strong>
            {gewaehlt.email && <span className="text-muted"> · {gewaehlt.email}</span>}
          </span>
          <button
            type="button"
            onClick={() => setGewaehlt(null)}
            className="cursor-pointer text-[11px] text-accent-deep hover:underline"
          >
            andere Person
          </button>
        </div>
      ) : (
        <>
          <input
            autoFocus
            value={suche}
            onChange={(e) => setSuche(e.target.value)}
            placeholder={`Name oder Adresse (${DOMAIN}) …`}
            aria-label="Wen einladen?"
            className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-[13px] outline-none focus:border-accent"
          />
          <div className="mt-1 flex flex-col">
            {treffer.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setGewaehlt({ personId: p.id, name: p.displayName })}
                className="cursor-pointer rounded px-1.5 py-1 text-left hover:bg-[#f3f2ef]"
              >
                {p.displayName} <span className="text-muted">· {p.email}</span>
              </button>
            ))}
            {adresseGueltig && !adresseBekannt && (
              <button
                type="button"
                onClick={() => setGewaehlt({ email: adresse, name: adresse })}
                className="cursor-pointer rounded px-1.5 py-1 text-left hover:bg-[#f3f2ef]"
              >
                + {adresse} einladen <span className="text-muted">· noch nicht in Work</span>
              </button>
            )}
            {suche.trim().length >= 2 && treffer.length === 0 && !adresseGueltig && (
              <span className="px-1.5 py-1 text-muted">
                Niemand gefunden. Einladen lassen sich nur NetCon-Benutzer mit einer Adresse auf{' '}
                {DOMAIN}.
              </span>
            )}
          </div>
        </>
      )}

      <label className="mt-2 flex cursor-pointer items-center gap-1.5">
        <input
          type="checkbox"
          checked={alsMitOwner}
          onChange={(e) => setAlsMitOwner(e.target.checked)}
        />
        als Mit-Owner eintragen{' '}
        <span className="text-muted">(begleitet das Vorhaben, nimmt ab)</span>
      </label>

      <textarea
        value={nachricht}
        onChange={(e) => setNachricht(e.target.value)}
        rows={2}
        placeholder="Ein Satz für die Mail (optional) – sonst steht dort die Beschreibung."
        aria-label="Nachricht in der Einladung"
        className="mt-2 w-full rounded-md border border-line bg-surface px-2 py-1.5 text-[12.5px] outline-none focus:border-accent"
      />

      <p className="mt-1 text-[11px] text-muted">
        Eingeladene sehen dieses Vorhaben und seine Aufgaben, bearbeiten Beschreibung und
        Meilensteine und schreiben in der Diskussion. Angemeldet wird mit dem NetCon-Konto.
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <Button
          variant="primary"
          size="sm"
          disabled={!gewaehlt || einladen.isPending}
          onClick={() => einladen.mutate()}
        >
          {einladen.isPending ? 'Lädt ein …' : 'Einladen & Mail schicken'}
        </Button>
        <Button size="sm" onClick={abbrechen}>
          Abbrechen
        </Button>
        {fehler && <span className="text-pool-sofort">{fehler}</span>}
      </div>
    </div>
  )
}
